/**
 * PM-6 — Step 5: validate → commit → per-row report (the final importer
 * step; closes the wizard built in PM-5). Models the ai-findings
 * `features/ai-findings-import/Step4Execute.tsx` async-commit + progress +
 * report pattern.
 *
 * Three phases:
 *  1. Pre-commit review — surfaces the four analyze buckets
 *     (`created | matched_unchanged | override_block | hard_error`,
 *     deriveCommitCounts). FB-36: a `hard_error` row no longer blocks the
 *     commit — it is SKIPPED server-side (the BE imports the valid rows and
 *     blocks/skips the errors). The Import button enables whenever there is
 *     ≥1 importable row (an INCLUDED, non-hard_error row); it disables only
 *     when 0 rows are importable (all errors / none included) or a commit is
 *     pending. A summary shows "N will be imported · M skipped (errors)".
 *  2. Commit — `POST /imports/commit` (PM-3) with the lifted state in the
 *     body (PM-D6 — edits travel in the body, never a re-upload) → importId.
 *     `override_block` rows are skipped for everyone UNLESS a `tenant_admin`
 *     confirms the OverrideConfirmDialog (PM-D5), which sets
 *     `allowOverride:true`. Non-admins have NO override affordance.
 *  3. Async progress + report — polls `GET /imports/{importId}` every ~750ms
 *     (useImportStatusQuery, terminal-detection) and renders the per-row
 *     result report (`created | matched_unchanged | blocked | overridden` +
 *     reason, PM-D19). One non-shifting bottom-overlay progress surface
 *     (feedback_progress_indication_minimal); a Cancel button posts
 *     `…/cancel` and the partial report stands (no rollback).
 *
 * Role gate: `useHasRole("tenant_admin")` — the same role primitive the
 * shipped `AccessGrantPanel` flow relies on. The BE re-enforces the override
 * server-side (PM-3): a forged client flag from a non-admin is rejected 422.
 */
import { useEffect, useMemo, useState, type ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useHasRole } from "@aeriosiq/auth-client";
import { useImportCommitMutation } from "./useImportCommit.js";
import {
  useImportCancelMutation,
  useImportStatusQuery,
} from "./useImportStatus.js";
import { collectOverrideRowDiffs, deriveCommitCounts } from "./overrideDiff.js";
import { OverrideConfirmDialog } from "./OverrideConfirmDialog.js";
import type {
  AnalyzeImportReport,
  CommitImportBody,
  ImportRowOutcome,
} from "./types.js";
import { CircleCheckIcon } from "@aeriosiq/ui/icons";

export interface Step5CommitProps {
  /** The latest analyze report — drives the pre-commit buckets + the gate. */
  report: AnalyzeImportReport;
  /** The ready-to-commit body MINUS `allowOverride` (PM-5's lifted state +
   *  `project`/`projectId` from the create-project context). Step 5 adds
   *  `allowOverride` from the override path. */
  commitBodyBase: Omit<CommitImportBody, "allowOverride">;
  /** Called once the job reaches a terminal status (host may refresh lists). */
  onDone?: (importId: string) => void;
  /** Notifies the host whenever a commit is in-flight or completed (a job has
   *  been started). The wizard uses this to lock the Review-step Back button
   *  once committing begins (FB-25): Back is allowed pre-commit, never after. */
  onCommittingChange?: (committing: boolean) => void;
}

const OUTCOME_LABEL: Record<ImportRowOutcome, string> = {
  created: "Created",
  matched_unchanged: "Matched (unchanged)",
  blocked: "Blocked",
  overridden: "Overridden",
};

function outcomeColor(
  outcome: ImportRowOutcome,
): "success" | "default" | "error" | "warning" {
  switch (outcome) {
    case "created":
      return "success";
    case "matched_unchanged":
      return "default";
    case "blocked":
      return "error";
    case "overridden":
      return "warning";
  }
}

function errMsg(e: unknown, fallback: string): string {
  if (e && typeof e === "object" && "body" in e) {
    const body = (e as { body?: { detail?: string; message?: string } }).body;
    const detail = body?.detail ?? body?.message;
    if (detail) return detail;
  }
  if (e && typeof e === "object" && "message" in e) {
    return String(e.message);
  }
  return fallback;
}

function CountChip({
  label,
  value,
  color,
  testid,
}: {
  label: string;
  value: number;
  color: "success" | "default" | "warning" | "error";
  testid: string;
}): ReactElement {
  return (
    <Chip
      variant="outlined"
      color={color}
      label={`${label}: ${String(value)}`}
      data-testid={testid}
    />
  );
}

export function Step5Commit({
  report,
  commitBodyBase,
  onDone,
  onCommittingChange,
}: Step5CommitProps): ReactElement {
  const isTenantAdmin = useHasRole("tenant_admin");

  const commit = useImportCommitMutation();
  const cancel = useImportCancelMutation();
  const [importId, setImportId] = useState<string | null>(null);
  const status = useImportStatusQuery(importId);

  const [overrideOpen, setOverrideOpen] = useState(false);

  const counts = useMemo(() => deriveCommitCounts(report), [report]);
  const overrideRows = useMemo(() => collectOverrideRowDiffs(report), [report]);

  const hasHardError = counts.hardError > 0;
  const hasOverrideBlock = counts.overrideBlock > 0;
  // FB-36 — hard_error rows are SKIPPED, not blocked. The importable rows are
  // the INCLUDED rows that are NOT a hard_error; the hard_error rows are
  // skipped server-side (BE imports the valid ones, blocks/skips the errors).
  const skippedCount = counts.hardError;
  const importableCount = counts.included - counts.hardError;
  const hasImportable = importableCount > 0;
  // CM-8 (CM-D14) — informational only; never gates the commit.
  const newContacts = report.newContacts;

  // Has the host already kicked off a job?
  const committing = commit.isPending || importId !== null;
  // Tell the wizard once a commit is in-flight/done so it can lock Back (FB-25).
  useEffect(() => {
    onCommittingChange?.(committing);
  }, [committing, onCommittingChange]);
  const job = status.data ?? null;
  const terminal =
    job !== null &&
    (job.status === "done" ||
      job.status === "cancelled" ||
      job.status === "failed");

  function startCommit(allowOverride: boolean): void {
    const body: CommitImportBody = { ...commitBodyBase };
    if (allowOverride) body.allowOverride = true;
    commit.mutate(body, {
      onSuccess: (res) => {
        setImportId(res.importId);
      },
    });
  }

  // FB-32 — has the admin already chosen CELLS to override on the Data grid? If
  // so, the granular `overrideFields` IS the opt-in and travels in the body —
  // commit directly, no all-or-nothing dialog.
  const hasPerCellOverride = (commitBodyBase.overrideFields?.length ?? 0) > 0;

  function handleCommitClick(): void {
    // FB-36 — hard_error rows no longer gate the commit; they are skipped
    // server-side. Only block when there is nothing importable at all.
    if (!hasImportable) return;
    // FB-32 — granular per-cell override already chosen on the grid → commit it
    // directly (the per-cell marks are the opt-in; the BE overrides only those
    // (row, field) values).
    if (isTenantAdmin && hasPerCellOverride) {
      startCommit(false);
      return;
    }
    // tenant_admin + override-block rows (none marked per-row) → the
    // all-or-nothing confirm dialog to opt INTO updating base data from the
    // sheet (FB-28). Everyone else commits directly: the override-block rows
    // import keeping the EXISTING base data (default).
    if (isTenantAdmin && hasOverrideBlock) {
      setOverrideOpen(true);
      return;
    }
    startCommit(false);
  }

  function handleOverrideConfirm(): void {
    setOverrideOpen(false);
    startCommit(true);
  }

  function handleCancel(): void {
    if (importId) cancel.mutate(importId);
  }

  // Notify the host once when the job goes terminal.
  const [notified, setNotified] = useState(false);
  if (terminal && importId && !notified) {
    setNotified(true);
    onDone?.(importId);
  }

  // ── Phase 3: committing / terminal report ──────────────────────────────
  if (committing) {
    return (
      <Box sx={{ position: "relative", minHeight: 320, pb: 8 }}>
        {/* Per-row report (fills in as the staged commit proceeds, PM-D19). */}
        {commit.isError && importId === null ? (
          <Alert severity="error" data-testid="commit-error" sx={{ mb: 2 }}>
            {errMsg(commit.error, "The import could not be started.")}
          </Alert>
        ) : null}

        {job === null ? (
          <Box sx={{ py: 4 }}>
            <Typography variant="body2" gutterBottom>
              Starting import…
            </Typography>
            <LinearProgress aria-label="Starting import" />
          </Box>
        ) : (
          <CommitReport
            outcomes={job.rows.map((r) => ({
              rowIndex: r.rowIndex,
              outcome: r.outcome,
              reason: r.reason ?? null,
            }))}
            terminal={terminal}
            status={job.status}
            error={job.error ?? null}
          />
        )}

        {/* ── ONE bottom-overlay progress surface (no per-row spinners, no
            layout shift) — feedback_progress_indication_minimal. ───────── */}
        <Paper
          elevation={3}
          data-testid="commit-progress-overlay"
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            p: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          {!terminal ? (
            <>
              <CircularProgress size={18} />
              <Typography variant="body2" sx={{ flex: 1 }}>
                {job
                  ? `Importing… ${String(job.progress.processed)} / ${String(
                      job.progress.total,
                    )}`
                  : "Starting…"}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={handleCancel}
                disabled={cancel.isPending || importId === null}
                data-testid="commit-cancel-button"
              >
                {cancel.isPending ? "Cancelling…" : "Cancel"}
              </Button>
            </>
          ) : (
            <>
              {job.status === "done" ? (
                <CircleCheckIcon color="success" fontSize="small" />
              ) : null}
              <Typography variant="body2" sx={{ flex: 1 }}>
                {job.status === "done"
                  ? "Import complete."
                  : job.status === "cancelled"
                    ? "Import cancelled — already-committed rows are kept."
                    : "Import failed — see the report below."}
              </Typography>
            </>
          )}
        </Paper>
      </Box>
    );
  }

  // ── Phase 1: pre-commit review + gate ──────────────────────────────────
  // The parks→turbines status tree (ReviewTree) renders ABOVE this block; here
  // we surface the four commit buckets, the hard-error gate, and the commit
  // button (the unchanged commit / override / model-confirm paths).
  return (
    <Stack spacing={3}>
      <Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <CountChip
            label="Will create"
            value={counts.created}
            color="success"
            testid="count-created"
          />
          <CountChip
            label="Matched (unchanged)"
            value={counts.matchedUnchanged}
            color="default"
            testid="count-matched"
          />
          <CountChip
            label="Keep existing (differs)"
            value={counts.overrideBlock}
            color="warning"
            testid="count-override-block"
          />
          <CountChip
            label="Hard errors"
            value={counts.hardError}
            color="error"
            testid="count-hard-error"
          />
        </Stack>
      </Box>

      {/* FB-36 — a summary of what will be imported vs skipped (errors). The
          hard_error rows no longer block the commit; they are skipped and the
          valid rows import. */}
      <Alert
        severity={hasImportable ? "info" : "error"}
        data-testid="import-summary"
      >
        {hasImportable
          ? `${String(importableCount)} ${
              importableCount === 1 ? "row" : "rows"
            } will be imported${
              skippedCount > 0
                ? ` · ${String(skippedCount)} skipped (error${
                    skippedCount === 1 ? "" : "s"
                  })`
                : ""
            }.`
          : `No rows can be imported — every included row has a hard error (e.g. a missing identifier, type, or park). Fix them in the preview grid — or change the mapping or selection — then import.`}
      </Alert>

      {hasHardError && hasImportable ? (
        <Alert severity="warning" data-testid="hard-error-skip-notice">
          {`${String(
            counts.hardError,
          )} row(s) have a hard error (e.g. a missing identifier, type, or park) and will NOT be imported — they're skipped. The valid rows import. Fix them in the preview grid — or change the mapping or selection — to include them.`}
        </Alert>
      ) : null}

      {hasOverrideBlock ? (
        isTenantAdmin ? (
          <Alert severity="info" data-testid="override-admin-notice">
            {`${String(
              counts.overrideBlock,
            )} row(s) match an existing turbine with different values. They will be imported keeping your existing base data. As a tenant admin you can instead override to update the base data from the sheet — you'll be asked to confirm, with a before/after diff per field.`}
          </Alert>
        ) : (
          <Alert severity="info" data-testid="override-block-notice">
            {`${String(
              counts.overrideBlock,
            )} row(s) match an existing turbine with different values. They will be imported keeping your existing base data (the sheet's differing values are not applied). A tenant administrator can override to update the base data from the sheet.`}
          </Alert>
        )
      ) : null}

      {newContacts && newContacts.count > 0 ? (
        <Alert severity="info" data-testid="new-contacts-callout">
          <Typography variant="body2" gutterBottom>
            {`${String(newContacts.count)} new ${
              newContacts.count === 1 ? "contact" : "contacts"
            } will be created — address-book entries only (these companies don't share the project, per the tenant-vs-contact resolution above):`}
          </Typography>
          <Box
            component="ul"
            sx={{ m: 0, pl: 3 }}
            data-testid="new-contacts-list"
          >
            {newContacts.names.map((name) => (
              <li key={name}>{name}</li>
            ))}
          </Box>
        </Alert>
      ) : null}

      <Box>
        <Button
          variant="contained"
          onClick={handleCommitClick}
          disabled={!hasImportable || commit.isPending}
          data-testid="commit-button"
        >
          {hasOverrideBlock && isTenantAdmin && !hasPerCellOverride
            ? "Review override & import"
            : "Import"}
        </Button>
      </Box>

      <OverrideConfirmDialog
        open={overrideOpen}
        rows={overrideRows}
        pending={commit.isPending}
        onConfirm={handleOverrideConfirm}
        onCancel={() => {
          setOverrideOpen(false);
        }}
      />
    </Stack>
  );
}

/* ── per-row result report (PM-D19) ────────────────────────────────────── */

function CommitReport({
  outcomes,
  terminal,
  status,
  error,
}: {
  outcomes: {
    rowIndex: number;
    outcome: ImportRowOutcome;
    reason: string | null;
  }[];
  terminal: boolean;
  status: "running" | "done" | "cancelled" | "failed";
  error: string | null;
}): ReactElement {
  return (
    <Box data-testid="commit-report">
      <Typography variant="h6" gutterBottom>
        {terminal
          ? status === "failed"
            ? "Import failed"
            : status === "cancelled"
              ? "Import cancelled"
              : "Import complete"
          : "Importing…"}
      </Typography>

      {status === "failed" && error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {outcomes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No rows reported yet.
        </Typography>
      ) : (
        <Table size="small" aria-label="Per-row import report">
          <TableHead>
            <TableRow>
              <TableCell>Row</TableCell>
              <TableCell>Outcome</TableCell>
              <TableCell>Reason</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {outcomes.map((r) => (
              <TableRow
                key={r.rowIndex}
                data-testid={`report-row-${String(r.rowIndex)}`}
              >
                <TableCell>{r.rowIndex + 1}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={outcomeColor(r.outcome)}
                    variant="outlined"
                    label={OUTCOME_LABEL[r.outcome]}
                  />
                </TableCell>
                <TableCell sx={{ color: "text.secondary" }}>
                  {r.reason ?? "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
